import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Activity, Plus, AlertTriangle, CheckCircle2, AlertCircle, Loader2, ExternalLink, Copy } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import {
  listIncidents,
  getIncident,
  createIncident,
  addIncidentUpdate,
  listSystemComponents,
  upsertSystemComponent,
} from "@/lib/incidents.functions";

export const Route = createFileRoute("/_authenticated/app/settings/status")({
  head: () => ({
    meta: [
      { title: "Status page — CairnOS" },
      { name: "description", content: "Manage components, declare incidents, and post updates." },
    ],
  }),
  component: StatusAdmin,
});

const COMPONENT_STATES = ["operational", "degraded", "partial_outage", "major_outage"] as const;
const SEVERITIES = ["minor", "major", "critical"] as const;
const INCIDENT_STATES = ["investigating", "identified", "monitoring", "resolved"] as const;

function StateBadge({ state }: { state: string }) {
  const map: Record<string, { cls: string; icon: any }> = {
    operational: { cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", icon: CheckCircle2 },
    degraded: { cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400", icon: AlertTriangle },
    partial_outage: { cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300", icon: AlertCircle },
    major_outage: { cls: "bg-destructive/10 text-destructive", icon: AlertCircle },
    investigating: { cls: "bg-destructive/10 text-destructive", icon: AlertCircle },
    identified: { cls: "bg-amber-500/10 text-amber-600", icon: AlertTriangle },
    monitoring: { cls: "bg-blue-500/10 text-blue-600", icon: Activity },
    resolved: { cls: "bg-emerald-500/10 text-emerald-600", icon: CheckCircle2 },
  };
  const m = map[state] ?? { cls: "bg-muted text-muted-foreground", icon: Activity };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`${m.cls} gap-1`}>
      <Icon className="h-3 w-3" /> {state.replace("_", " ")}
    </Badge>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    minor: "bg-blue-500/10 text-blue-600",
    major: "bg-amber-500/10 text-amber-600",
    critical: "bg-destructive/10 text-destructive",
  };
  return <Badge variant="outline" className={map[severity] ?? ""}>{severity}</Badge>;
}

function StatusAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listComponentsFn = useServerFn(listSystemComponents);
  const upsertComponentFn = useServerFn(upsertSystemComponent);
  const listIncidentsFn = useServerFn(listIncidents);
  const createIncidentFn = useServerFn(createIncident);

  const [compOpen, setCompOpen] = useState(false);
  const [compEditing, setCompEditing] = useState<any>(null);
  const [incOpen, setIncOpen] = useState(false);
  const [inspectingId, setInspectingId] = useState<string | null>(null);

  const componentsQ = useQuery({
    queryKey: ["status-components", tenantId],
    enabled: !!tenantId,
    queryFn: () => listComponentsFn({ data: { tenantId: tenantId! } }),
  });

  const incidentsQ = useQuery({
    queryKey: ["status-incidents", tenantId],
    enabled: !!tenantId,
    queryFn: () => listIncidentsFn({ data: { tenantId: tenantId!, limit: 50 } }),
    refetchInterval: 30_000,
  });

  const compMut = useMutation({
    mutationFn: (payload: any) => upsertComponentFn({ data: payload }),
    onSuccess: () => {
      toast.success("Component saved");
      setCompOpen(false); setCompEditing(null);
      void qc.invalidateQueries({ queryKey: ["status-components", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const stateMut = useMutation({
    mutationFn: ({ component, newState }: { component: any; newState: string }) =>
      upsertComponentFn({
        data: {
          tenantId: tenantId!,
          id: component.id,
          name: component.name,
          slug: component.slug,
          description: component.description ?? undefined,
          currentState: newState as any,
          displayOrder: component.display_order,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["status-components", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const incMut = useMutation({
    mutationFn: (payload: any) => createIncidentFn({ data: payload }),
    onSuccess: (res) => {
      toast.success("Incident declared");
      setIncOpen(false);
      setInspectingId(res.id);
      void qc.invalidateQueries({ queryKey: ["status-incidents", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const components = componentsQ.data ?? [];
  const incidents = incidentsQ.data ?? [];
  const openIncidents = incidents.filter((i: any) => i.state !== "resolved");

  const publicUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/status?tenant=${tenantId}`
      : "";

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="h-6 w-6" /> Status page
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Operate a tenant-scoped public status page. Components, incidents, and updates feed
            it in real time.
          </p>
        </div>
        <div className="flex gap-2">
          {tenantId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                toast.success("Public URL copied");
              }}
            >
              <Copy className="h-4 w-4 mr-2" /> Copy public URL
            </Button>
          )}
          {tenantId && (
            <Button asChild variant="outline" size="sm">
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" /> View page
              </a>
            </Button>
          )}
        </div>
      </div>

      {openIncidents.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" /> {openIncidents.length} open incident{openIncidents.length === 1 ? "" : "s"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {openIncidents.map((i: any) => (
              <div
                key={i.id}
                className="flex items-center justify-between text-sm cursor-pointer hover:bg-muted/40 p-2 -mx-2 rounded"
                onClick={() => setInspectingId(i.id)}
              >
                <div className="flex items-center gap-2">
                  <SeverityBadge severity={i.severity} />
                  <StateBadge state={i.state} />
                  <span className="font-medium">{i.title}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  started {formatDistanceToNow(new Date(i.started_at), { addSuffix: true })}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Components</CardTitle>
            <CardDescription>What's running, and how it's doing right now.</CardDescription>
          </div>
          <Button size="sm" onClick={() => { setCompEditing(null); setCompOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> New component
          </Button>
        </CardHeader>
        <CardContent>
          {componentsQ.isLoading ? (
            <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
          ) : components.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">
              No components yet. Add one for each public-facing surface (API, dashboard, fulfillment, etc.)
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Quick set</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {components.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.slug}</TableCell>
                    <TableCell><StateBadge state={c.current_state} /></TableCell>
                    <TableCell>
                      <Select
                        value={c.current_state}
                        onValueChange={(v) => stateMut.mutate({ component: c, newState: v })}
                      >
                        <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COMPONENT_STATES.map((s) => (
                            <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setCompEditing(c); setCompOpen(true); }}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Incidents</CardTitle>
            <CardDescription>Declare an incident, post updates, mark resolved.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setIncOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Declare incident
          </Button>
        </CardHeader>
        <CardContent>
          {incidentsQ.isLoading ? (
            <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
          ) : incidents.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">No incidents on record.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Visible</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Resolved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((i: any) => (
                  <TableRow
                    key={i.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setInspectingId(i.id)}
                  >
                    <TableCell className="font-medium">{i.title}</TableCell>
                    <TableCell><SeverityBadge severity={i.severity} /></TableCell>
                    <TableCell><StateBadge state={i.state} /></TableCell>
                    <TableCell className="text-xs">
                      {i.customer_visible
                        ? <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">public</Badge>
                        : <Badge variant="outline" className="bg-muted">internal</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(i.started_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {i.resolved_at ? formatDistanceToNow(new Date(i.resolved_at), { addSuffix: true }) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ComponentEditor
        open={compOpen}
        editing={compEditing}
        tenantId={tenantId}
        onClose={() => { setCompOpen(false); setCompEditing(null); }}
        onSubmit={(p: any) => compMut.mutate(p)}
        submitting={compMut.isPending}
      />

      <IncidentComposer
        open={incOpen}
        tenantId={tenantId}
        components={components}
        onClose={() => setIncOpen(false)}
        onSubmit={(p: any) => incMut.mutate(p)}
        submitting={incMut.isPending}
      />

      <IncidentInspector
        incidentId={inspectingId}
        tenantId={tenantId}
        onClose={() => setInspectingId(null)}
      />
    </div>
  );
}

function ComponentEditor({
  open, editing, tenantId, onClose, onSubmit, submitting,
}: any) {
  const [name, setName] = useState(editing?.name ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [currentState, setCurrentState] = useState<string>(editing?.current_state ?? "operational");
  const [displayOrder, setDisplayOrder] = useState<number>(editing?.display_order ?? 100);

  // Reset when opening with a different target.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useStateSync(editing?.id ?? "__new__", () => {
    setName(editing?.name ?? "");
    setSlug(editing?.slug ?? "");
    setDescription(editing?.description ?? "");
    setCurrentState(editing?.current_state ?? "operational");
    setDisplayOrder(editing?.display_order ?? 100);
  });

  const submit = () => {
    if (!tenantId) return;
    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }
    onSubmit({
      tenantId,
      id: editing?.id,
      name: name.trim(),
      slug: slug.trim().toLowerCase(),
      description: description.trim() || undefined,
      currentState,
      displayOrder,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit component" : "New component"}</DialogTitle>
          <DialogDescription>Displayed publicly on your status page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cmp-name">Name</Label>
            <Input id="cmp-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Public API" />
          </div>
          <div>
            <Label htmlFor="cmp-slug">Slug</Label>
            <Input id="cmp-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="public-api" />
          </div>
          <div>
            <Label htmlFor="cmp-desc">Description</Label>
            <Textarea id="cmp-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cmp-state">State</Label>
              <Select value={currentState} onValueChange={setCurrentState}>
                <SelectTrigger id="cmp-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPONENT_STATES.map((s) => (<SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cmp-order">Display order</Label>
              <Input id="cmp-order" type="number" value={displayOrder} onChange={(e) => setDisplayOrder(Number(e.target.value))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentComposer({
  open, tenantId, components, onClose, onSubmit, submitting,
}: any) {
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<string>("minor");
  const [affected, setAffected] = useState<string[]>([]);
  const [customerVisible, setCustomerVisible] = useState(true);
  const [message, setMessage] = useState("");

  useStateSync(open, () => {
    setTitle(""); setSeverity("minor"); setAffected([]);
    setCustomerVisible(true); setMessage("");
  });

  const toggle = (slug: string) =>
    setAffected((prev) => (prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]));

  const submit = () => {
    if (!tenantId) return;
    if (!title.trim() || !message.trim()) {
      toast.error("Title and initial message are required");
      return;
    }
    onSubmit({
      tenantId,
      title: title.trim(),
      severity,
      affectedComponents: affected,
      customerVisible,
      initialMessage: message.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Declare incident</DialogTitle>
          <DialogDescription>
            Posts immediately to the public status page and notifies subscribers when visible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="inc-title">Title</Label>
            <Input id="inc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Elevated API error rates" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="inc-sev">Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger id="inc-sev"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-between border rounded px-3 py-2">
              <div>
                <div className="text-sm font-medium">Public</div>
                <div className="text-[10px] text-muted-foreground">Show on status page</div>
              </div>
              <Switch checked={customerVisible} onCheckedChange={setCustomerVisible} />
            </div>
          </div>
          <div>
            <Label>Affected components</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {components.length === 0 ? (
                <div className="text-xs text-muted-foreground">No components defined yet.</div>
              ) : components.map((c: any) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => toggle(c.slug)}
                  className={`text-xs px-2 py-1 rounded border transition ${
                    affected.includes(c.slug)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="inc-msg">Initial update</Label>
            <Textarea
              id="inc-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="We're investigating elevated 5xx rates on the public API."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Declare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentInspector({
  incidentId, tenantId, onClose,
}: { incidentId: string | null; tenantId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const getFn = useServerFn(getIncident);
  const addFn = useServerFn(addIncidentUpdate);

  const [newState, setNewState] = useState<string>("monitoring");
  const [updateMessage, setUpdateMessage] = useState("");

  const q = useQuery({
    queryKey: ["incident", incidentId],
    enabled: !!incidentId && !!tenantId,
    queryFn: () => getFn({ data: { tenantId: tenantId!, id: incidentId! } }),
  });

  const addMut = useMutation({
    mutationFn: () =>
      addFn({
        data: {
          tenantId: tenantId!,
          id: incidentId!,
          state: newState as any,
          message: updateMessage.trim(),
        },
      }),
    onSuccess: () => {
      toast.success("Update posted");
      setUpdateMessage("");
      void qc.invalidateQueries({ queryKey: ["incident", incidentId] });
      void qc.invalidateQueries({ queryKey: ["status-incidents", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  useStateSync(incidentId, () => {
    setNewState(q.data?.incident?.state === "resolved" ? "investigating" : "monitoring");
    setUpdateMessage("");
  });

  const inc = q.data?.incident;
  const updates = q.data?.updates ?? [];

  return (
    <Sheet open={!!incidentId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{inc?.title ?? "Loading…"}</SheetTitle>
          <SheetDescription>
            {inc && (
              <div className="flex items-center gap-2 mt-2">
                <SeverityBadge severity={inc.severity} />
                <StateBadge state={inc.state} />
                <span className="text-xs">started {format(new Date(inc.started_at), "PPpp")}</span>
              </div>
            )}
          </SheetDescription>
        </SheetHeader>
        {inc && (
          <div className="mt-4 space-y-4">
            {inc.affected_components?.length > 0 && (
              <div className="text-xs">
                <span className="text-muted-foreground">Affected: </span>
                {inc.affected_components.map((c: string) => (
                  <Badge key={c} variant="secondary" className="ml-1 text-[10px]">{c}</Badge>
                ))}
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-2">Post Update</h3>
              <div className="space-y-2">
                <Select value={newState} onValueChange={setNewState}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INCIDENT_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  rows={3}
                  placeholder="What's the latest?"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!updateMessage.trim()) return toast.error("Update message required");
                    addMut.mutate();
                  }}
                  disabled={addMut.isPending}
                >
                  {addMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Post update
                </Button>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-2">Timeline</h3>
              <div className="space-y-3">
                {updates.map((u: any) => (
                  <div key={u.id} className="border-l-2 border-muted pl-3">
                    <div className="flex items-center gap-2 text-xs">
                      <StateBadge state={u.state} />
                      <span className="text-muted-foreground">{format(new Date(u.created_at), "PPpp")}</span>
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{u.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

import { useEffect, useRef } from "react";
function useStateSync(dep: any, fn: () => void) {
  const prev = useRef<any>(undefined);
  useEffect(() => {
    if (prev.current !== dep) {
      prev.current = dep;
      fn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}